import React from 'react'
import { Menu } from '@base-ui-components/react/menu'
import styled, { css } from 'styled-components'
import { Icon } from '~common/components/icon'

export interface DropdownMenuItem {
    id: string
    label: string
    onClick?: (event?: React.MouseEvent | React.KeyboardEvent) => void
    disabled?: boolean
    separator?: boolean
    icon?: string | React.ReactNode
    href?: string
}

export interface DropdownMenuProps {
    trigger: React.ReactElement
    items: DropdownMenuItem[]
    placement?: 'top' | 'bottom' | 'left' | 'right'
    align?: 'start' | 'center' | 'end'
    sideOffset?: number
    width?: string
    className?: string
}

export default function DropdownMenu({
    trigger,
    items,
    align = 'end',
    sideOffset = 8,
    width = '100%',
    className,
}: DropdownMenuProps) {
    if (typeof window === 'undefined') {
        return trigger
    }

    return (
        <Menu.Root>
            <Menu.Trigger
                render={(triggerProps, state) =>
                    React.cloneElement(trigger, triggerProps)
                }
            />
            <Menu.Portal>
                <MenuPositionerWrapper>
                    <Menu.Positioner
                        positionMethod="fixed"
                        side={'bottom'}
                        sideOffset={sideOffset}
                        align={align}
                    >
                        <MenuPopup
                            className={className}
                            data-baseui-menu="true"
                            style={{ width }}
                        >
                            {/* <Menu.Arrow>
                                <ArrowSvg />
                            </Menu.Arrow> */}
                            {items.map((item) =>
                                item.separator ? (
                                    <MenuSeparator key={item.id} />
                                ) : (
                                    <MenuItem
                                        key={item.id}
                                        id={item.id}
                                        disabled={item.disabled}
                                        href={item.href}
                                        as={item.href ? 'a' : undefined}
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            item.onClick?.(event)
                                        }}
                                        data-disabled={
                                            item.disabled ? 'true' : undefined
                                        }
                                    >
                                        {item.icon &&
                                            (typeof item.icon === 'string' ? (
                                                <Icon
                                                    name={item.icon}
                                                    size="small"
                                                    iconColor={({ theme }) =>
                                                        theme.colors.info
                                                    }
                                                />
                                            ) : (
                                                item.icon
                                            ))}
                                        {item.label}
                                    </MenuItem>
                                ),
                            )}
                        </MenuPopup>
                    </Menu.Positioner>
                </MenuPositionerWrapper>
            </Menu.Portal>
        </Menu.Root>
    )
}

const MenuPositionerWrapper = styled.div`
    z-index: 2147483647 !important;
    position: relative;
`

const MenuPopup = styled(Menu.Popup)`
    background-color: ${({ theme }) => theme.colors.background.bg1};
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    min-width: 140px;
    padding: ${({ theme }) => theme.spacing[1]};
    transform-origin: top center;
    animation: slideIn 0.15s ease-out;

    @keyframes slideIn {
        from {
            opacity: 0;
            transform: scale(0.95) translateY(-4px);
        }
        to {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    }
`

const MenuItem = styled(Menu.Item)<{ id?: string }>`
    gap: ${({ theme }) => theme.spacing[3]};
    width: 100%;
    padding: ${({ theme }) => theme.spacing[3]};
    background: none;
    border: none;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    transition: all ${({ theme }) => theme.transitions.fast};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    display: flex;
    align-items: center;
    position: relative;
    box-sizing: border-box;

    &:hover:not([data-disabled]) {
        background-color: ${({ theme }) => theme.colors.greyScale[400]};
    }

    &:focus {
        /* outline: 2px solid ${({ theme }) => theme.colors.border.focus}; */
        outline-offset: -2px;
        background-color: ${({ theme }) => theme.colors.greyScale[50]};
    }

    &[data-disabled] {
        &:hover {
            background: transparent;
            cursor: default;
        }
    }
    ${({ id }) =>
        id === 'add-new' &&
        css`
            color: ${({ theme }) => theme.colors.greyScale[100]};
        `}
`

const MenuSeparator = styled(Menu.Separator)`
    height: 1px;
    background-color: ${({ theme }) => theme.colors.greyScale[300]};
    margin: ${({ theme }) => theme.spacing[2]} 0;
`

const StyledArrow = styled.div`
    color: ${({ theme }) => theme.colors.background.bg1};
    filter: drop-shadow(0 -1px 0 ${({ theme }) => theme.colors.border.primary});
    margin-bottom: -1px;
`
